import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, Grid2, useTheme } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  VendorsAnalyticsFilters,
  VendorsAnalyticsSelectedFilters,
  parseQueryParamsForVendorsAnalyticsFilters,
} from "../../../../../models/Dashboard/VendorDashboard/VendorsAnalyticsFilters";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import { formatAmount } from "../../../../../views/components/common/functions/formatDate";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";

const OPS_VENDORS_ANALYTICS_FILTERS_KEY = "OPS_VENDORS_ANALYTICS_FILTERS_KEY";

const VendorsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    vendorsAnalyticsDashboardStore,
    employeeStore,
    vendorStore,
    userStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: VendorsAnalyticsFilters =
    parseQueryParamsForVendorsAnalyticsFilters(
      localStorage.getItem(OPS_VENDORS_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(OPS_VENDORS_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams,
    );

  const [filters, setFilters] =
    useState<VendorsAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<VendorsAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      OPS_VENDORS_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters),
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.vendor_id) {
      filters.vendor_id.forEach((vendor_id) =>
        newFilters.push({ id: "vendor_id", value: vendor_id.value }),
      );
    }

    if (filters.admin_id) {
      filters.admin_id.forEach((admin_id) =>
        newFilters.push({ id: "admin_id", value: admin_id.value }),
      );
    }

    if (filters.product_id) {
      filters.product_id.forEach((product_id) =>
        newFilters.push({ id: "product_id", value: product_id.value }),
      );
    }

    setSelectedFiltersData(newFilters);
  };

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
      }));

      await vendorsAnalyticsDashboardStore.getAllAnalyticsData({
        vendor_id: selectedFilters.vendor_id,
        admin_id: selectedFilters.admin_id,
        product_id: selectedFilters.product_id,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "product_id":
        setFilters((prev) => ({
          ...prev,
          product_id:
            prev.product_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "vendor_id":
        setFilters((prev) => ({
          ...prev,
          vendor_id:
            prev.vendor_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "admin_id":
        setFilters((prev) => ({
          ...prev,
          admin_id:
            prev.admin_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      vendor_id: [],
      admin_id: [],
      product_id: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleVendorIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        vendorStore.getVendors({
          page_no: 1,
          limit: 30,
          search_text: searchText,
          sortBy: "created_date",
          sortType: SortType.DESC,
        });
      }, 500);
  };

  const handleAdminSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2) {
      apiTimer = setTimeout(() => {
        employeeStore.fetchEmployee({
          page_no: 1,
          limit: 30,
          search_text: searchText,
          sortBy: "name",
          sortType: SortType.ASC,
        });
      }, 500);
    }
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_OPS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: { xs: 2, sm: 3, md: 4 },
                borderRadius: 2,
                marginBottom: 1,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Vendors
                  </CustomTypography>
                  <AppSearchDropdownMulti
                    borderColor={theme.palette.customColors.blackBorder}
                    isMultiSelect={true}
                    title="Select Permissions"
                    fontSize="0.8rem"
                    value={filters.vendor_id || []}
                    list={vendorStore.vendors?.map((item) => {
                      return {
                        id: String(item.id!.toString()),
                        value: String(item.name!),
                      };
                    })}
                    handleSearch={handleVendorIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        vendor_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Admin
                  </CustomTypography>
                  <AppSearchDropdownMulti
                    borderColor={theme.palette.customColors.blackBorder}
                    isMultiSelect={true}
                    title="Select Admins"
                    fontSize="0.8rem"
                    value={filters.admin_id || []}
                    list={
                      employeeStore.employees?.map((item) => ({
                        id: String(item.id),
                        value: String(item.name),
                      })) ?? []
                    }
                    handleSearch={handleAdminSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        admin_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>
              <Box sx={{ paddingTop: "15px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1, sm: 2 },
                    borderRadius: 2,
                  }}
                >
                  {selectedFiltersData.map((filter, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                        borderRadius: 5,
                        border: "1px solid #E2E2E2",
                        height: { xs: "28px", sm: "30px" },
                        paddingRight: "5px",
                        minWidth: { xs: "120px", sm: "auto" },
                      }}
                    >
                      <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.blackBorder}
                        style={{
                          paddingLeft: "10px",
                          paddingRight: "5px",
                        }}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: "100px", sm: "150px", md: "200px" },
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        {filter.value}
                      </CustomTypography>
                      <Button
                        onClick={() => handleRemoveFilter(index)}
                        sx={{
                          width: { xs: "12px", sm: "14px" },
                          height: { xs: "12px", sm: "14px" },
                          minWidth: { xs: "12px", sm: "14px" },
                          minHeight: { xs: "12px", sm: "14px" },
                          fontSize: { xs: "12px", sm: "15px" },
                          padding: "0px",
                          border: "1px solid #E2E2E2",
                          borderRadius: "50%",
                          backgroundColor: theme.palette.customColors.white,
                          cursor: "pointer",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                          },
                        }}
                      >
                        ×
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ marginBottom: 3 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 2 },
                marginBottom: 4,
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {vendorsAnalyticsDashboardStore.inward_tats.map((item, index) => (
                <Box
                  key={`${item.label}-${index}`}
                  sx={{
                    flex: {
                      xs: "1 1 140px",
                      sm: "1 1 180px",
                      md: "1 1 200px",
                    },
                    maxWidth: {
                      xs: "calc(50% - 8px)",
                      sm: "calc(33.33% - 16px)",
                      md: "calc(25% - 16px)",
                      lg: "calc(20% - 16px)",
                      xl: "calc(16.66% - 16px)",
                    },
                    minWidth: { xs: "140px", sm: "160px", md: "180px" },
                  }}
                >
                  <TypeFilterCard
                    title={item.label}
                    message={item.info}
                    value={
                      item.label === "Net Purchase Value"
                        ? formatAmount(item.value)
                        : (item.value ?? "0")
                    }
                    color={""}
                    icon={"https://content.swageazy.com/icons/boxes_green.png"}
                    onClick={(title) => {
                      switch (title) {
                        case "PO Count":
                          const po_count_url = `/report/data?title=${encodeURI(
                            "PO Count",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/po-count&${searchParams.toString()}`;
                          window.open(po_count_url, "_blank");
                          break;
                        case "Total Purchase Value":
                          const purchase_value_url = `/report/data?title=${encodeURI(
                            "Total Purchase Value",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/total-purchase-value&${searchParams.toString()}`;
                          window.open(purchase_value_url, "_blank");
                          break;
                        case "On-Time Delivery Score":
                          const dipatch_url = `/report/data?title=${encodeURI(
                            "On-Time Delivery Score",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/on-time-delivery-ratings&${searchParams.toString()}`;
                          window.open(dipatch_url, "_blank");
                          break;
                        case "Order Accuracy Score":
                          const delivery_url = `/report/data?title=${encodeURI(
                            "Order Accuracy Score",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/order-accuracy-score&${searchParams.toString()}`;
                          window.open(delivery_url, "_blank");
                          break;
                        case "Raw Inward Rejection %":
                          const received_supply_url = `/report/data?title=${encodeURI(
                            "Raw Inward Rejection %",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/raw-inward-rejection-percentage&${searchParams.toString()}`;
                          window.open(received_supply_url, "_blank");
                          break;
                        case "Print Inward Rejection %":
                          const vendor_tat_url = `/report/data?title=${encodeURI(
                            "Print Inward Rejection %",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/print-inward-rejection-percentage&${searchParams.toString()}`;
                          window.open(vendor_tat_url, "_blank");
                          break;
                        case "Lead Time":
                          const lead_time_url = `/report/data?title=${encodeURI(
                            "Lead Time Score",
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/lead-time&${searchParams.toString()}`;
                          window.open(lead_time_url, "_blank");
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Grid2>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(VendorsDashboard);
